import type { IEventRepository } from "@/core/repositories/interfaces/IEventRepository";
import type { IPlaceRepository } from "@/core/repositories/interfaces/IPlaceRepository";
import type { IMediaService } from "@/core/services/interfaces/IMediaService";
import type { IPlaceService } from "@/core/services/interfaces/IPlaceService";
import { GooglePlacesService } from "@/core/services/google-places.service";
import { PLACE_SELECT_SEARCH_LIMIT } from "@/core/services/types/place.types";
import type {
  CreatePlaceInput,
  CreatePlaceResult,
  PlaceEditor,
  PlaceLibraryKpis,
  PlaceLibraryTab,
  PlaceLibraryTabCounts,
  PlaceLibrarySortState,
  PlaceListPage,
  PlaceOpeningHours,
  PlaceSelectOption,
  PlaceTaxonomy,
  SavePlaceOpeningHoursResult,
  SyncPlaceOpeningHoursResult,
  UpdatePlaceContentInput,
  UpdatePlaceContentResult,
  UpdatePlaceInternalNotesInput,
  UpdatePlaceCoverImageInput,
  UpdatePlaceStatusInput,
  UpdatePlaceTaxonomyInput,
  UpdatePlaceTaxonomyResult,
  UploadPlaceCoverImageResult,
} from "@/core/services/types/place.types";
import {
  isDeletableEditorialStatus,
  type DeleteSignalContentResult,
} from "@/core/services/types/article.types";
import type { LibraryPageSize } from "@/core/services/types/library-pagination.types";

function normalizeGooglePlaceId(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export class PlaceService implements IPlaceService {
  constructor(
    private readonly repository: IPlaceRepository,
    private readonly mediaService: IMediaService,
    private readonly eventRepository: IEventRepository,
    private readonly googlePlacesService: GooglePlacesService = new GooglePlacesService(),
  ) {}

  async createPlace(input: CreatePlaceInput = {}): Promise<CreatePlaceResult> {
    if (input.status === "SIGNAL") {
      const { status: _status, ...createInput } = input;
      const created = await this.repository.createAdmin({
        ...createInput,
        status: "DRAFT",
      });

      if (!created) {
        return { success: false, error: "create_failed" };
      }

      const promoted = await this.repository.updateAdminStatus(created.id, {
        status: "SIGNAL",
      });

      if (!promoted) {
        return { success: false, error: "signal_status_failed" };
      }

      return { success: true, data: promoted };
    }

    const created = await this.repository.createAdmin(input);

    if (!created) {
      return { success: false, error: "create_failed" };
    }

    return { success: true, data: created };
  }

  async deleteSignalContent(id: string): Promise<DeleteSignalContentResult> {
    if (!id.trim()) {
      return { success: false, error: "not_found" };
    }

    const place = await this.repository.findAdminById(id);
    if (!place) {
      return { success: false, error: "not_found" };
    }

    if (!isDeletableEditorialStatus(place.status)) {
      return { success: false, error: "not_deletable" };
    }

    const deleted = await this.repository.deleteAdmin(id);
    if (!deleted) {
      return { success: false, error: "delete_failed" };
    }

    return { success: true };
  }

  async getForEdit(id: string): Promise<PlaceEditor | null> {
    if (!id.trim()) return null;
    return this.repository.findAdminById(id);
  }

  async updatePlaceContent(
    id: string,
    input: UpdatePlaceContentInput,
  ): Promise<UpdatePlaceContentResult> {
    if (!id.trim()) return { success: false, error: "unknown" };

    const existing = await this.repository.findAdminById(id);
    const result = await this.repository.updateAdminContent(id, input);

    if (!result.ok) {
      return { success: false, error: result.error };
    }

    const googlePlaceIdChanged =
      normalizeGooglePlaceId(existing?.google_place_id) !==
      normalizeGooglePlaceId(input.google_place_id);

    let syncedLinkedEventsCount = 0;
    if (googlePlaceIdChanged) {
      syncedLinkedEventsCount =
        await this.eventRepository.resyncLinkedEventsForPlace(
          id,
          result.data.department_name,
        );
    }

    return {
      success: true,
      data: result.data,
      syncedLinkedEventsCount,
      googlePlaceIdChanged,
    };
  }

  async updatePlaceStatus(
    id: string,
    input: UpdatePlaceStatusInput,
  ): Promise<PlaceEditor | null> {
    if (!id.trim()) return null;
    return this.repository.updateAdminStatus(id, input);
  }

  async updatePlaceInternalNotes(
    id: string,
    input: UpdatePlaceInternalNotesInput,
  ): Promise<PlaceEditor | null> {
    if (!id.trim()) return null;
    return this.repository.updateAdminInternalNotes(id, input);
  }

  async updatePlaceCoverImage(
    id: string,
    input: UpdatePlaceCoverImageInput,
  ): Promise<PlaceEditor | null> {
    if (!id.trim()) return null;
    return this.repository.updateAdminCoverImage(id, input);
  }

  async uploadPlaceCoverImage(
    id: string,
    file: File,
  ): Promise<UploadPlaceCoverImageResult> {
    if (!id.trim()) {
      return { success: false, error: "Missing place id" };
    }

    const uploadResult = await this.mediaService.uploadPlaceCoverImage(id, file);

    if (!uploadResult.success) {
      return { success: false, error: uploadResult.error };
    }

    const updated = await this.repository.updateAdminCoverImage(id, {
      coverImageUrl: uploadResult.publicUrl,
    });

    if (!updated) {
      return {
        success: false,
        error: "Failed to save cover image URL",
      };
    }

    return { success: true, data: updated };
  }

  async listByLibraryTab(
    tab: PlaceLibraryTab,
    page: number,
    pageSize: LibraryPageSize,
    search?: string,
    sort?: PlaceLibrarySortState,
  ): Promise<PlaceListPage> {
    return this.repository.findByLibraryTab(tab, page, pageSize, search, sort);
  }

  async countByLibraryTab(): Promise<PlaceLibraryTabCounts> {
    return this.repository.countByLibraryTab();
  }

  async getLibraryStats(): Promise<{
    tabCounts: PlaceLibraryTabCounts;
    kpis: PlaceLibraryKpis;
  }> {
    const [tabCounts, publishedThisMonth] = await Promise.all([
      this.repository.countByLibraryTab(),
      this.repository.countPublishedThisMonth(),
    ]);

    return {
      tabCounts,
      kpis: {
        total: tabCounts.ALL,
        pendingReview: tabCounts.PENDING_REVIEW,
        publishedThisMonth,
      },
    };
  }

  async listAllForSelect(): Promise<PlaceSelectOption[]> {
    return this.repository.findAllForSelect();
  }

  async getSelectOptionById(id: string): Promise<PlaceSelectOption | null> {
    return this.repository.findSelectOptionById(id);
  }

  async searchForSelect(query: string): Promise<PlaceSelectOption[]> {
    return this.repository.searchForSelect(query, PLACE_SELECT_SEARCH_LIMIT);
  }

  async getPlaceTaxonomy(id: string): Promise<PlaceTaxonomy> {
    if (!id.trim()) {
      return { artFormIds: [], artisticPeriodIds: [], culturalAreaIds: [], exhibitionTypeIds: [] };
    }
    return this.repository.findTaxonomy(id);
  }

  async updatePlaceTaxonomy(
    id: string,
    input: UpdatePlaceTaxonomyInput,
  ): Promise<UpdatePlaceTaxonomyResult> {
    if (!id.trim()) {
      return { success: false, error: "Missing place id" };
    }

    const ok = await this.repository.replaceTaxonomy(id, input);

    if (!ok) {
      return { success: false, error: "Failed to update taxonomy" };
    }

    return { success: true };
  }

  async syncOpeningHoursFromGoogle(id: string): Promise<SyncPlaceOpeningHoursResult> {
    const place = await this.repository.findAdminById(id);

    if (!place) {
      return { success: false, error: "Place not found" };
    }

    if (!place.google_place_id?.trim()) {
      return { success: false, error: "No Google Place ID configured" };
    }

    const detailsResult = await this.googlePlacesService.getPlaceDetails(
      place.google_place_id,
    );

    if (!detailsResult.success) {
      return { success: false, error: detailsResult.error };
    }

    const updated = await this.repository.updateOpeningHours(
      id,
      detailsResult.place.openingHours,
    );

    if (!updated) {
      return { success: false, error: "Failed to save opening hours" };
    }

    return { success: true, data: updated };
  }

  async savePlaceOpeningHours(
    id: string,
    hours: PlaceOpeningHours,
  ): Promise<SavePlaceOpeningHoursResult> {
    if (!id.trim()) {
      return { success: false, error: "Missing place id" };
    }

    const updated = await this.repository.updateOpeningHours(id, hours);

    if (!updated) {
      return { success: false, error: "Failed to save opening hours" };
    }

    return { success: true, data: updated };
  }
}
